import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { visitLanguageSchema } from "./visit";

// Live transcript gap clips (web recorder, 30/10/2026). When the live
// transcript loses its connection (a locked phone, a network drop) for
// longer than its provider can catch up on, the recorder sends the audio of
// that gap, kept on the device, here: it is transcribed with the batch
// speech-to-text path the full recording uses (same routing by language,
// optional diarization) and the timed lines go back into the live view. The
// clip is display only and never stored: the note is still written from the
// full recording after Stop.
export const GAP_CLIP_MAX_SECONDS = 300;
// Mono PCM16 WAV at 16 kHz (the recorder downsamples its 24 kHz capture).
export const GAP_CLIP_SAMPLE_RATE = 16000;
export const GAP_CLIP_MAX_BYTES =
  44 + GAP_CLIP_MAX_SECONDS * GAP_CLIP_SAMPLE_RATE * 2;
export const GAP_CLIP_MAX_SEGMENTS = 1000;

export const gapClipInputSchema = z.strictObject({
  // Base64 WAV, at most GAP_CLIP_MAX_BYTES once decoded.
  audio: z
    .string()
    .min(4)
    .max(Math.ceil(GAP_CLIP_MAX_BYTES / 3) * 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Expected base64"),
  // The recording's input language ("eng", "fra", "auto"...).
  language: visitLanguageSchema,
  diarize: z.boolean().default(false),
});
export type GapClipInput = z.output<typeof gapClipInputSchema>;

export const gapClipSegmentSchema = z.object({
  // From the start of the clip.
  startMs: z.int().nonnegative(),
  endMs: z.int().nonnegative(),
  speaker: z.int().nonnegative().nullable(),
  text: z.string().max(10000),
});
export const gapClipTranscriptSchema = z.object({
  segments: z.array(gapClipSegmentSchema).max(GAP_CLIP_MAX_SEGMENTS),
  language: z.string().max(16),
});
export type GapClipTranscript = z.output<typeof gapClipTranscriptSchema>;

export const gapTranscriptionContract = {
  createGapTranscription: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      TOO_MANY_REQUESTS: { message: "Too many gap clips" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Gap transcription unavailable" },
    })
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: gapClipInputSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/transcriptions/gap-clips",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(gapClipTranscriptSchema),
};
